/*************************************************************************
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { getTileCanvasRefPerPage } from "selectors/pagedViewSelectors";
import { createURLFromBuffer } from "dc-preview-utils/src/Utils/UrlUtils";
import { getViewRotationAngle } from "selectors/documentSelectors";
import { transformRectPDFToView, transformRectViewToPDF } from "../utils/PageUtils";
import * as stores from "stores";
/**
 *
 * @param { Object of type Image } img
 * @param { Angle at which view is rotated } viewRotationAngle
 * @returns The canvas on which image is drawn
 */

export const getRotatedCanvas = (img, viewRotationAngle) => {
  const canvas = document.createElement("canvas");
  const shouldInterchangeDimensions = viewRotationAngle === 90 || viewRotationAngle === 270;
  const imgHeight = shouldInterchangeDimensions ? img.width : img.height;
  const imgWidth = shouldInterchangeDimensions ? img.height : img.width;
  canvas.height = imgHeight;
  canvas.width = imgWidth;
  const ctx = canvas.getContext("2d");
  ctx.translate(imgWidth / 2, imgHeight / 2);
  ctx.rotate((viewRotationAngle * Math.PI) / 180);
  if (shouldInterchangeDimensions) {
    ctx.translate(-imgHeight / 2, -imgWidth / 2);
  } else {
    ctx.translate(-imgWidth / 2, -imgHeight / 2);
  }
  ctx.drawImage(img, 0, 0);
  return canvas;
};

export const drawUpdatedRect = (stores, pageIndex, rect, buffer, mimeType) => {
  const imgUrl = createURLFromBuffer(buffer, mimeType);
  drawUpdatedRectOnCanvas(stores, pageIndex, rect, imgUrl);
};

const drawUpdatedRectOnCanvas = (stores, pageIndex, rect, imgUrl) => {
  const editCanvasRef = getTileCanvasRefPerPage(stores, pageIndex);
  if (editCanvasRef) {
    const editCanvasRefContext = editCanvasRef.getContext("2d");
    const img = new Image();
    img.src = imgUrl;
    img.onload = () => {
      const viewRotationAngle = getViewRotationAngle(stores);
      const rotatedImage = viewRotationAngle !== 0 ? getRotatedCanvas(img, viewRotationAngle) : img;
      editCanvasRefContext.drawImage(rotatedImage, rect.left, rect.top);
      // clear up memory
      URL.revokeObjectURL(imgUrl);
    };
  }
};

/**
 * @param {*} rectInViewSpace
 * @param {*} zoomLevel
 * @param {*} pageIndex
 * @returns This method takes the tile rect in view space and if the view is rotated converts the points in view space
 * to doc space, and returns the file rects in view space without rotation.
 * If the view is not rotated it simply returns the tile rect in view space
 */

export const getComputedTileRect = (rectInViewSpace, zoomLevel, pageIndex) => {
  const viewRotationAngle = getViewRotationAngle(stores);
  let finalRectInViewSpace = rectInViewSpace;
  if (viewRotationAngle) {
    finalRectInViewSpace = getTileRectWithoutRotation(rectInViewSpace, pageIndex, stores, zoomLevel);
  }

  return finalRectInViewSpace;
};

/**
 *
 * @param {Accepts tile rect in view space with rotation angle} rectInViewSpace
 * @param {*} pageIndex
 * @param {*} stores
 * @param {*} zoomLevel
 * This method returns the tile rect in view space without rotation
 */
const getTileRectWithoutRotation = (rectInViewSpace, pageIndex, stores, zoomLevel) => {
  const tileRectInDocSpace = transformRectViewToPDF(
    {
      l: rectInViewSpace.left,
      r: rectInViewSpace.right,
      t: rectInViewSpace.top,
      b: rectInViewSpace.bottom
    },
    pageIndex,
    stores,
    zoomLevel
  );

  return transformRectPDFToView(
    tileRectInDocSpace.left,
    tileRectInDocSpace.right,
    tileRectInDocSpace.top,
    tileRectInDocSpace.bottom,
    pageIndex,
    stores,
    zoomLevel,
    {
      viewRotation: 0
    }
  );
};

// convert view space points to doc space and then add rotation angle 0 for view rotated pdfs
